'use client';

import { cn } from '@/lib/utils';
import { returnsSchema } from '@/lib/validationSchema';
import { ProductOpenedOptionEnum, ReasonForReturnOptionEnum } from '@/types/enums';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import DateInput from '../ui/DateInput';
import Input from '../ui/Input';
import Radio from '../ui/Radio';
import Textarea from '../ui/Textarea';
import TextSmall from '../ui/TextSmall';

type FormData = z.infer<typeof returnsSchema>;

export default function ReturnsForm({ className }: { className?: string }) {
    const t = useTranslations();
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const today = new Date();

    const productOpenedOptions = [
        { value: ProductOpenedOptionEnum.Yes, label: t('Form.yes') },
        { value: ProductOpenedOptionEnum.No, label: t('Form.no') },
    ];

    const reasonForReturnOptions = [
        { value: ReasonForReturnOptionEnum.DeadOnArrival, label: t('Form.deadOnArrival') },
        { value: ReasonForReturnOptionEnum.OrderError, label: t('Form.orderError') },
        {
            value: ReasonForReturnOptionEnum.Faulty,
            label: t.rich('Form.faulty', {
                hint: (chunks) => <span className="text-grey">{chunks}</span>,
            }),
        },
        { value: ReasonForReturnOptionEnum.WrongItem, label: t('Form.wrongItem') },
    ];

    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedProductOpenedOption, setSelectedProductOpenedOption] = useState<string>(
        ProductOpenedOptionEnum.Yes,
    );
    const [selectedReasonOption, setSelectedReasonOption] = useState<string>(
        ReasonForReturnOptionEnum.DeadOnArrival,
    );

    const {
        handleSubmit,
        register,
        reset,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        resolver: zodResolver(returnsSchema),
    });

    const onSubmit = async (data: FormData) => {
        const formData = new FormData();
        formData.append('formType', 'returns');
        formData.append('name', data.name);
        formData.append('email', data.email);
        formData.append('phone', data.phone || '');
        formData.append('orderId', data.orderId);
        formData.append('orderDate', data.orderDate?.toString() || '');
        formData.append('productName', data.productName);
        formData.append('productCode', data.productCode);
        formData.append('quantity', data.quantity ? data.quantity.toString() : '');
        formData.append('reasonForReturn', data.reasonForReturn);
        formData.append('productOpened', data.productOpened);
        formData.append('comment', data.comment || '');

        try {
            const response = await fetch('/submit-form.php', {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Form submission result:', result);
                setSuccessMessage(t('Form.submissionSuccess'));

                reset();
                setSelectedDate(null);

                setTimeout(() => {
                    setSuccessMessage(null);
                }, 3000);
            } else {
                console.error('Form submission failed:', response.statusText);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const handleOrderDateChange = (date: Date | null) => {
        setSelectedDate(date);
        setValue('orderDate', date ?? today);
    };

    const handleOptionChange = (fieldName: 'reasonForReturn' | 'productOpened', option: string) => {
        if (fieldName === 'productOpened') setSelectedProductOpenedOption(option);
        if (fieldName === 'reasonForReturn') setSelectedReasonOption(option);
        setValue(fieldName, option);
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message ? t(`FieldErrors.${errors[fieldName]?.message}`) : '';

    return (
        <form
            className={cn(
                'flex flex-col max-lg:py-6 max-lg:px-4 max-lg:gap-6 max-lg:bg-white/80 max-lg:backdrop-blur-sm lg:p-[60px] bg-white bg-opacity-90 shadow-m backdrop-blur-md backdrop-brightness-100 gap-12 relative z-10',
                className,
            )}
            onSubmit={handleSubmit(onSubmit)}
            id="returns-form-trigger"
        >
            <div>
                <h3 className="text-dark-grey drop-shadow-sm leading-tight text-h1 max-lg:text-h2">
                    {t('Metadata.Returns.headings.productReturns')}
                </h3>
                <TextSmall>{t('Form.pleaseCompleteForm')}</TextSmall>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 w-full gap-x-[100px] lg:gap-y-11 max-lg:gap-y-5 gap-y-7">
                <Input
                    {...register('name')}
                    type="text"
                    name="name"
                    id="name"
                    placeholder={t('Form.fullName')}
                    autoComplete="false"
                    error={getFieldError('name')}
                    required
                />
                <Input
                    {...register('email')}
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t('Form.yourEmail')}
                    autoComplete="false"
                    error={getFieldError('email')}
                    required
                />
                <Input
                    {...register('phone')}
                    type="phone"
                    name="phone"
                    id="phone"
                    placeholder={t('Form.phone')}
                    autoComplete="false"
                    error={getFieldError('phone')}
                />
                <Input
                    {...register('orderId')}
                    type="number"
                    name="orderId"
                    id="orderId"
                    placeholder={t('Form.orderId')}
                    autoComplete="false"
                    error={getFieldError('orderId')}
                    required
                />
                <DateInput
                    {...register('orderDate')}
                    selectedDate={selectedDate}
                    placeholder={t('Form.orderDate')}
                    onChange={handleOrderDateChange}
                />
                <div className="empty hidden lg:block" />
                <Input
                    {...register('productName')}
                    type="text"
                    name="productName"
                    id="productName"
                    placeholder={t('Form.productName')}
                    autoComplete="false"
                    error={getFieldError('productName')}
                    required
                />
                <Input
                    {...register('productCode')}
                    type="number"
                    name="productCode"
                    id="productCode"
                    placeholder={t('Form.productCode')}
                    autoComplete="false"
                    error={getFieldError('productCode')}
                    required
                />
                <Input
                    {...register('quantity')}
                    type="number"
                    name="quantity"
                    id="quantity"
                    placeholder={t('Form.quantity')}
                    autoComplete="false"
                    error={getFieldError('quantity')}
                    required
                />
            </div>
            <Radio
                {...register('reasonForReturn')}
                className="2xl:[&>p]:w-1/6 xl:[&>p]:w-1/5 lg:[&>p]:w-1/3 max-lg:[&>p]:w-full max-lg:[&>p]:mb-2"
                options={reasonForReturnOptions}
                label={t('Form.reasonForReturn')}
                onChange={(option) => handleOptionChange('reasonForReturn', option)}
                value={selectedReasonOption}
                required
            />
            <Radio
                {...register('productOpened')}
                className="2xl:[&>p]:w-1/6 xl:[&>p]:w-1/5 lg:[&>p]:w-1/3 max-lg:[&>p]:w-full max-lg:[&>p]:mb-2"
                options={productOpenedOptions}
                label={t('Form.productIsOpened')}
                onChange={(option) => handleOptionChange('productOpened', option)}
                value={selectedProductOpenedOption}
                required
            />
            <Textarea
                {...register('comment')}
                name="comment"
                id="comment"
                placeholder={t('Form.faultyOrOtherDetails')}
                autoComplete="false"
                error={getFieldError('comment')}
                rows={1}
            />
            <Checkbox
                {...register('consent')}
                className="mt-4"
                id="consent"
                name="consent"
                label={t('Form.consent')}
                required
            />
            <div className="flex max-md:flex-col-reverse items-start justify-between max-lg:gap-4 gap-10">
                <div
                    className="relative"
                    style={{
                        visibility: successMessage ? 'visible' : 'hidden',
                    }}
                >
                    <div className="text-brand">{t('Form.submissionSuccess')}</div>
                </div>
                <Button
                    className="w-fit min-w-[100px] md:min-w-40 h-fit self-end"
                    title={t('Buttons.send')}
                    variant="send"
                    withArrow
                    type="submit"
                    disabled={isSubmitting}
                />
            </div>
        </form>
    );
}
